import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  Autocomplete,
  Chip,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { accountingApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';

interface ShipmentTemplate {
  id: string;
  name: string;
  platform: string;
  isDefault?: boolean;
  createdAt: string;
  updatedAt?: string;
}

interface ShipmentTemplatesModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
}

const ShipmentTemplatesModal: React.FC<ShipmentTemplatesModalProps> = ({
  open,
  onClose,
  companyId
}) => {
  const { t, i18n } = useTranslation('accounting');
  const dateLocale = i18n.language?.toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR';
  const [templates, setTemplates] = useState<ShipmentTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Yeni şablon ekleme için state'ler
  const [newTemplateName, setNewTemplateName] = useState<string>('');
  const [selectedPlatform, setSelectedPlatform] = useState<string>('n11');
  const [isAddingTemplate, setIsAddingTemplate] = useState<boolean>(false);

  // Desteklenen platformlar
  const platforms = [
    { value: 'n11', color: '#ff6b35' },
    { value: 'hepsiburada', color: '#ff6000' },
    { value: 'trendyol', color: '#f27a1a' },
    { value: 'amazon', color: '#ff9900' },
    { value: 'other', color: '#666666' }
  ];

  // Modal açıldığında şablonları yükle
  useEffect(() => {
    if (open && companyId) {
      loadShipmentTemplates();
    }
  }, [open, companyId]);

  // Kargo şablonlarını SQL API'den yükle
  const loadShipmentTemplates = async () => {
    if (!companyId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await accountingApi.get(
        `/api/shipmenttemplate/by-platform?platform=${selectedPlatform}`
      );
      const result = response.data;

      if (result.success && result.data) {
        const templatesList: ShipmentTemplate[] = result.data.map((item: any) => ({
          id: item.id,
          name: item.name || '',
          platform: item.platform || 'other',
          isDefault: item.isDefault || false,
          createdAt: item.createdAt || new Date().toISOString(),
          updatedAt: item.updatedAt
        }));

        // Platform ve isme göre sırala
        templatesList.sort((a, b) => {
          if (a.platform !== b.platform) {
            return a.platform.localeCompare(b.platform);
          }
          return a.name.localeCompare(b.name);
        });

        setTemplates(templatesList);
      } else {
        setTemplates([]);
      }
    } catch (error) {
      console.error('Kargo şablonları yüklenirken hata:', error);
      setError(t('stock.shipmentTemplates.errors.load'));
    } finally {
      setLoading(false);
    }
  };

  // Yeni şablon ekleme
  const handleAddTemplate = async () => {
    if (!companyId || !newTemplateName.trim()) {
      setError(t('stock.shipmentTemplates.errors.templateNameRequired'));
      return;
    }

    // Aynı platform ve isimde şablon var mı kontrol et
    const existingTemplate = templates.find(
      t => t.platform === selectedPlatform && t.name.toLowerCase() === newTemplateName.trim().toLowerCase()
    );

    if (existingTemplate) {
      setError(t('stock.shipmentTemplates.errors.templateExists', { platform: selectedPlatform.toUpperCase(), name: newTemplateName }));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const isFirstForPlatform = templates.filter(t => t.platform === selectedPlatform).length === 0;

      const response = await accountingApi.post(`/api/shipmenttemplate/create`, {
        Name: newTemplateName.trim(),
        Platform: selectedPlatform,
        IsDefault: isFirstForPlatform
      });
      const result = response.data;

      if (result.success) {
        // Şablonları yeniden yükle
        await loadShipmentTemplates();

        // Form'u temizle
        setNewTemplateName('');
        setSelectedPlatform('n11');
        setIsAddingTemplate(false);

        console.log('Yeni kargo şablonu eklendi');
      } else {
        setError(t('stock.shipmentTemplates.errors.add'));
      }
    } catch (error) {
      console.error('Kargo şablonu eklenirken hata:', error);
      setError(t('stock.shipmentTemplates.errors.add'));
    } finally {
      setSaving(false);
    }
  };

  // Şablon silme
  const handleDeleteTemplate = async (templateId: string) => {
    if (!companyId) return;

    setSaving(true);
    setError(null);

    try {
      const response = await accountingApi.post(`/api/shipmenttemplate/delete`, {
        Id: templateId
      });
      const result = response.data;

      if (result.success) {
        // Local state'den kaldır
        setTemplates(prev => prev.filter(t => t.id !== templateId));
        console.log('Kargo şablonu silindi:', templateId);
      } else {
        setError(t('stock.shipmentTemplates.errors.delete'));
      }
    } catch (error) {
      console.error('Kargo şablonu silinirken hata:', error);
      setError(t('stock.shipmentTemplates.errors.delete'));
    } finally {
      setSaving(false);
    }
  };

  // Varsayılan şablon değiştirme
  const handleSetDefault = async (templateId: string, platform: string) => {
    if (!companyId) return;

    setSaving(true);
    setError(null);

    try {
      const response = await accountingApi.post(`/api/shipmenttemplate/set-default`, {
        Id: templateId,
        Platform: platform
      });
      const result = response.data;

      if (result.success) {
        // Local state'i güncelle
        setTemplates(prev => prev.map(t => ({
          ...t,
          isDefault: t.platform === platform ? t.id === templateId : t.isDefault
        })));
        console.log('Varsayılan kargo şablonu güncellendi:', templateId);
      } else {
        setError(t('stock.shipmentTemplates.errors.updateDefault'));
      }
    } catch (error) {
      console.error('Varsayılan şablon güncellenirken hata:', error);
      setError(t('stock.shipmentTemplates.errors.updateDefault'));
    } finally {
      setSaving(false);
    }
  };

  // Platform rengini al
  const getPlatformColor = (platform: string) => {
    const platformData = platforms.find(p => p.value === platform);
    return platformData?.color || '#666666';
  };

  // Platform adını al
  const getPlatformLabel = (platform: string) => {
    const platformLabels: Record<string, string> = {
      n11: 'N11',
      hepsiburada: 'Hepsiburada',
      trendyol: 'Trendyol',
      amazon: 'Amazon',
      other: t('stock.shipmentTemplates.otherPlatform')
    };
    return platformLabels[platform] || platform.toUpperCase();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalShippingIcon color="primary" />
          {t('stock.shipmentTemplates.title')}
        </Box>
        <GuideButton guideKey="inventory.shipmentTemplates" />
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Yeni şablon ekleme formu */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', border: '1px solid #ddd', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            {t('stock.shipmentTemplates.addTitle')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
            <Autocomplete
              size="small"
              options={platforms}
              getOptionLabel={(option) => getPlatformLabel(option.value)}
              value={platforms.find(p => p.value === selectedPlatform) || platforms[0]}
              onChange={(_, newValue) => setSelectedPlatform(newValue?.value || 'n11')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('stock.shipmentTemplates.platform')}
                  sx={{ width: 150 }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Chip
                    label={getPlatformLabel(option.value)}
                    size="small"
                    sx={{
                      bgcolor: option.color,
                      color: 'white',
                      mr: 1
                    }}
                  />
                  {getPlatformLabel(option.value)}
                </Box>
              )}
              disabled={saving}
            />

            <TextField
              size="small"
              label={t('stock.shipmentTemplates.templateName')}
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              placeholder={t('stock.shipmentTemplates.templatePlaceholder')}
              sx={{ flexGrow: 1 }}
              disabled={saving}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddTemplate();
                }
              }}
            />

            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} /> : <AddIcon />}
              onClick={handleAddTemplate}
              disabled={saving || !newTemplateName.trim()}
            >
              {saving ? t('stock.shipmentTemplates.adding') : t('stock.shipmentTemplates.add')}
            </Button>
          </Box>
        </Box>

        {/* Mevcut şablonlar listesi */}
        <Typography variant="h6" gutterBottom>
          {t('stock.shipmentTemplates.currentTemplates', { count: templates.length })}
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : templates.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <LocalShippingIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              {t('stock.shipmentTemplates.emptyTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('stock.shipmentTemplates.emptySubtitle')}
            </Typography>
          </Box>
        ) : (
          <List>
            {templates.map((template, index) => (
              <React.Fragment key={template.id}>
                <ListItem>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                    <Chip
                      label={getPlatformLabel(template.platform)}
                      size="small"
                      sx={{
                        bgcolor: getPlatformColor(template.platform),
                        color: 'white',
                        minWidth: 80
                      }}
                    />
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" fontWeight={template.isDefault ? 'bold' : 'normal'}>
                            {template.name}
                          </Typography>
                          {template.isDefault && (
                            <Chip label={t('stock.shipmentTemplates.default')} size="small" color="primary" />
                          )}
                        </Box>
                      }
                      secondary={t('stock.shipmentTemplates.createdAt', { date: new Date(template.createdAt).toLocaleDateString(dateLocale) })}
                    />
                  </Box>
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {!template.isDefault && (
                        <Button
                          size="small"
                          onClick={() => handleSetDefault(template.id, template.platform)}
                          disabled={saving}
                        >
                          {t('stock.shipmentTemplates.setDefault')}
                        </Button>
                      )}
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteTemplate(template.id)}
                        disabled={saving}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < templates.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t('stock.common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShipmentTemplatesModal;
